import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit, EventEmitter, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { deserialize } from '../../../../utils/json-typescript-mapper';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiError } from '../../../../models/slim-response-error.model';
import { ApiBaseResponse } from '../../../../models/slim-status-response.model';
import { CellPhoneUpdateDialog } from '../../../../profile/models/cellphone-update-dialog.model';
import { SmsSendVerificationResponse } from '../../../../registration-3/models/sms-send-verification-response.model';
import { RegistrationPipeService } from '../../../../registration-3/services/registration-pipe.service';
import { RegistrationService } from '../../../../registration-3/services/registration.service';
import { SessionStorageService } from '../../../../shared-services/services/session-storage.service';
import { SnackBarService } from '../../../../shared-services/services/snack-bar.service';
import { AuthResponse } from '../../../models/auth-response.model';
import { CellPhoneUpdateDialogComponent } from '../cellphone-update-dialog/cellphone-update-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { UnsubscribeComponent } from '../../../../shared-components/components/unsubscribe/unsubscribe.abstract';
import { GlobalCTATrackerService } from 'app/shared-services/services/global-cta-tracker.service';

@Component({
    selector: 'app-cellphone-validation',
    templateUrl: './cellphone-validation.component.html',
    styleUrls: ['./cellphone-validation.component.scss'],
    standalone: false
})
export class CellphoneValidationComponent extends UnsubscribeComponent implements OnInit {
    @Input() inStepper = false;
    @Output() cellphoneValidated = new EventEmitter<void>();

    public requesting = false;

    public cellphoneValidationFormGroup: UntypedFormGroup;
    public sendPhoneCode$: Observable<SmsSendVerificationResponse>;

    public login$: Observable<AuthResponse>;
    public verifyPhoneCode$: Observable<any>;

    constructor(
        private globalCTATrackerService: GlobalCTATrackerService,
        private router: Router,
        private formBuilder: UntypedFormBuilder,
        private sessionStorageService: SessionStorageService,
        private dialog: MatDialog,
        private snackBarService: SnackBarService,
        private registrationService: RegistrationService,
        private registrationPipeService: RegistrationPipeService
    ) {
        super();
    }

    ngOnInit(): void {
        this.emitEvent('Sign In - View OTP Validation Page');
        this.prepareForm();

        const authToken = this.sessionStorageService.getAuthToken();
        if (authToken && authToken.isPhoneVerified) {
            this.router.navigate(['dashboard']);
        }
    }

    public prepareForm(): void {
        this.cellphoneValidationFormGroup = this.formBuilder.group({
            cellphoneValidationCodeControl: ['', Validators.required]
        });
    }

    public submit(): void {
        if (this.cellphoneValidationFormGroup.invalid) {
            this.cellphoneValidationFormGroup.markAllAsTouched();
        } else {
            const phoneValidationCode = this.cellphoneValidationFormGroup.get('cellphoneValidationCodeControl').value;

            this.verifyPhoneCode$ = this.registrationService.verifyPhoneCodeB2C(phoneValidationCode)
                .pipe(takeUntil(this.unsubscriber));

            this.verifyPhoneCode$.subscribe(() => {
                this.emitEvent('Sign In - OTP Validation Complete');

                const authToken = this.sessionStorageService.getAuthToken();

                if (authToken && authToken.isPhoneVerified) {
                    this.router.navigate(['dashboard']);
                } else {
                    this.router.navigate(['login']);
                }
            }, (error: HttpErrorResponse) => {
                const slimError = deserialize(ApiBaseResponse, error.error.Status);
                this.processPhoneCodeErrors(slimError);
            });
        }
    }

    public sendNewPhoneCode(): void {
        this.requesting = true;

        this.sendPhoneCode$ = this.registrationService.sendPhoneCodeB2C();

        this.sendPhoneCode$
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((res) => {
                this.requesting = false;
                this.emitEvent('Sign In - Resend OTP');

                if (res.phoneNumberChangeRequired) {
                    this.snackBarService.openErrorSnackbar('Please update your phone number');
                }
            });
    }

    private processPhoneCodeErrors(verifyPhoneCodeSlimStatusResponse: ApiBaseResponse): void {
        const cellphoneVerificationErrors =
            verifyPhoneCodeSlimStatusResponse.errors
                .filter((error: ApiError) => {
                    return error.message === 'SMS Verification code does not match.';
                });

        if (cellphoneVerificationErrors.length) {
            this.cellphoneValidationFormGroup.controls['cellphoneValidationCodeControl']
                .setErrors({ wrongCode: true });
        }
    }

    public cellphoneConfirmationCodeRequired(): boolean {
        return this.cellphoneValidationFormGroup
            .get('cellphoneValidationCodeControl')
            .hasError('required');
    }

    public cellphoneConfirmationCodeMatchRequired(): boolean {
        return this.cellphoneValidationFormGroup
            .get('cellphoneValidationCodeControl')
            .hasError('wrongCode');
    }

    public unableToCommunicate(): boolean {
        return this.cellphoneValidationFormGroup
            .get('cellphoneValidationCodeControl')
            .hasError('communicationFailure');
    }

    public subscriptionInviteKeyUsed(): boolean {
        return this.cellphoneValidationFormGroup
            .get('cellphoneValidationCodeControl')
            .hasError('keyUsed');
    }

    public updatePhoneNumber(): void {
        const registrationUser = this.registrationPipeService.getUser();

        const dialogRef = this.dialog.open(CellPhoneUpdateDialogComponent, {
            data: new CellPhoneUpdateDialog({
                phoneNumber: registrationUser.phoneNumber
            }),
            autoFocus: false,
            disableClose: false,
            width: '500px'
        });

        dialogRef.afterClosed().subscribe((updated: boolean) => {
            if (updated) {
                this.emitEvent('Sign In - OTP Validation Update Phone Number');
                this.sendNewPhoneCode();
            }
        });
    }

    private emitEvent(event: string): void {
        this.globalCTATrackerService.eventTrackerV2('cellphone-validation-cta', event);
    }
}
